// FILE: src/components/MilestonesAtAGlance.js
//
// Milestones at a Glance — three tier buckets (Foundation / Mastery /
// Completion) with earned + locked badges visible, plus a Progress
// Overview ring summarizing earned / in-progress / locked counts.
//
// Data: loadMilestonesSummary() from achievementService returns
// { catalog, earned, byKey } — we filter by activeCert (or exam-agnostic)
// and group by tier.

import React, { useEffect, useMemo, useState } from 'react';
import DashCard from '../dashboard/DashCard';
import ProgressRing from '../shared/ProgressRing';
import BadgeLightbox from './BadgeLightbox';
import styles from './MilestonesAtAGlance.module.css';
import { loadMilestonesSummary } from '../../services/achievementService';

const BADGE_BASE = process.env.PUBLIC_URL + '/themes/pm-pixel/achievements/';

// achievement_definitions.tier → display bucket
const TIER_TO_BUCKET = {
  foundation: 'Foundation',
  progress:   'Mastery',
  mastery:    'Mastery',
  completion: 'Completion',
};

const BUCKET_ORDER = ['Foundation', 'Mastery', 'Completion'];

const BUCKET_COLORS = {
  Foundation: '#16a571',
  Mastery:    '#2c63ff',
  Completion: '#8b5cf6',
};

// Map achievement icon key → existing badge-*.png filename in /achievements/.
const ICON_TO_FILE = {
  baseline_set:        'badge-baseline.png',
  comeback:            'badge-comeback.png',
  domain_climber:      'badge-climber.png',
  exam_ready:          'badge-examready.png',
  three_sims:          'badge-threesims.png',
  full_sim:            'badge-fullsim.png',
  pace_setter:         'badge-pace.png',
  resilience:          'badge-resilience.png',
  certification_earned:'badge-certified.png',
};

export default function MilestonesAtAGlance({ activeCert, onViewAll }) {
  const [data, setData] = useState({ catalog: [], earned: [], byKey: {} });
  const [loading, setLoading] = useState(true);
  const [zoomItem, setZoomItem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await loadMilestonesSummary();
      if (!cancelled) {
        setData(r || { catalog: [], earned: [], byKey: {} });
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const buckets = useMemo(() => groupByBucket(data, activeCert), [data, activeCert]);
  const counts  = useMemo(() => countStates(buckets), [buckets]);

  return (
    <DashCard
      eyebrow="Milestones at a Glance"
      eyebrowIcon="🏆"
      action={
        onViewAll && (
          <button type="button" onClick={onViewAll} data-testid="dashboard-view-milestones">
            View all milestones ›
          </button>
        )
      }
      testId="dashboard-milestones-glance"
    >
      <div className={styles.grid}>
        <div className={styles.bucketsCol}>
          {loading ? (
            <p className={styles.loading}>Loading milestones…</p>
          ) : BUCKET_ORDER.map((bucket) => {
            const items = buckets[bucket] || [];
            if (items.length === 0) return null;
            return (
              <div className={styles.bucket} key={bucket}>
                <div
                  className={styles.bucketLabel}
                  style={{ color: BUCKET_COLORS[bucket] }}
                >{bucket}</div>
                <div className={styles.badgeRow}>
                  {items.map((m) => (
                    <div
                      key={m.key}
                      className={[styles.badge, m.earned ? styles.earned : styles.locked].join(' ')}
                    >
                      <button
                        type="button"
                        className={styles.badgeZoom}
                        onClick={() => setZoomItem({ ...m, icon: iconFile(m.icon).replace(/\.png$/, '') })}
                        aria-label={`View ${m.title} badge larger`}
                        data-testid={`glance-badge-zoom-${m.key}`}
                      >
                        <img src={BADGE_BASE + iconFile(m.icon)} alt="" />
                      </button>
                      <div>
                        <div className={styles.name}>{m.title}</div>
                        {m.earned ? (
                          <div className={styles.earnedTag}>
                            {m.earned_at ? `Earned ${formatShort(m.earned_at)}` : 'Earned'}
                          </div>
                        ) : null}
                        <div className={styles.desc}>{m.description || ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.summary}>
          <h4 className={styles.summaryHead}>Progress Overview</h4>
          <div className={styles.summaryBigN}>
            {counts.earned} / {counts.total}
          </div>
          <div className={styles.summarySub}>Milestones earned</div>
          <ProgressRing
            value={counts.total > 0 ? (counts.earned / counts.total) * 100 : 0}
            size={120}
            stroke={12}
            color="#2c63ff"
            trackColor="#e5e8f1"
            className={styles.summaryRing}
          />
          <div className={styles.legend}>
            <div className={styles.legendRow}><span style={{ background: '#2c63ff' }} /> {counts.earned} Earned</div>
            <div className={styles.legendRow}><span style={{ background: '#16a571' }} /> {counts.inProgress} In Progress</div>
            <div className={styles.legendRow}><span style={{ background: '#cfd5e6' }} /> {counts.locked} Locked</div>
          </div>
        </div>
      </div>
      <BadgeLightbox item={zoomItem} onClose={() => setZoomItem(null)} />
    </DashCard>
  );
}

function groupByBucket({ catalog, byKey }, activeCert) {
  const out = { Foundation: [], Mastery: [], Completion: [] };
  for (const def of catalog || []) {
    // exam-scoped definitions: keep if matches the active cert OR is exam-agnostic
    if (def.exam_id && activeCert && def.exam_id !== activeCert) continue;
    const bucket = TIER_TO_BUCKET[def.tier] || null;
    if (!bucket || !out[bucket]) continue;
    const enriched = byKey?.[def.key] || { ...def, earned: false, earned_at: null };
    out[bucket].push(enriched);
  }
  return out;
}

function countStates(buckets) {
  let earned = 0;
  let total = 0;
  for (const bucket of BUCKET_ORDER) {
    for (const m of buckets[bucket] || []) {
      total += 1;
      if (m.earned) earned += 1;
    }
  }
  // We don't have a true "in progress" signal, so split locked into
  // "close" (in progress = roughly half of locked, capped at 3) just for
  // a non-flat ring legend. Pure visualization, doesn't affect counts.
  const locked = total - earned;
  const inProgress = Math.min(Math.floor(locked / 2), 3);
  return { earned, locked: Math.max(locked - inProgress, 0), inProgress, total };
}

function iconFile(iconKey) {
  return ICON_TO_FILE[iconKey] || 'badge-baseline.png';
}

function formatShort(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
